import { useFormik } from 'formik';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import EditIcon from '@/images/EditIcon.svg?react';
import SaveIcon from '@/images/SaveIcon.svg?react';
import CloseIcon from '@/images/CloseIconNew.svg?react';
import { DepositAccountStatus } from '../../../ui/DepositAccountStatus/DepositAccountStatus';
import { formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { editDepositAccountValidationSchema } from './const/editDepositAccountValidationSchema';
import { DEPOSIT_ACCOUNT_STATUS_DB } from '@/const/wire';
import { Loader } from '@/components/Loader/Loader';
import { DepositAccountType } from '@/api/v1/organization/deposit-accounts/getDepositAccountsApi';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { IconButton } from '@/components/IconButton/IconButton';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { Box } from '@/components/Box/Box';
import { PrimaryDetailsPanel } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanel';
import { PrimaryDetailsPanelColumn } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanelColumn';
import { COLORS } from '@/styles/colors';
import { PrimaryDetailsPanelHeader } from '@/bundle/shared/components/PrimaryDetailsPanel/styles';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { updateDepositAccount } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/_DepositAccountDetailsPage/api/api';
import { DepositAccountUpdateDataType } from '@/api/v1/organization/deposit-accounts/editDepositAccountApi';

type DepositAccountDetailsType = {
  depositAccount: DepositAccountType;
};

export const DepositAccountDetails = ({ depositAccount }: DepositAccountDetailsType) => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState(false);
  const isActiveDepositAccount = depositAccount?.status === DEPOSIT_ACCOUNT_STATUS_DB.ACTIVE;
  const formattedDate = formatDate(depositAccount?.date_created);
  const { name, status, ref_id, created_by } = depositAccount || {};

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['update_deposit_account'],
    mutationFn: (payload: DepositAccountUpdateDataType) => {
      return updateDepositAccount(id, payload);
    },
    onSuccess(response) {
      if (response.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.EditDepositAccountSuccess);

      setIsEdit(false);
      queryClient.setQueryData(['load_deposit_account', id], response);
    },
  });

  const formik = useFormik({
    validationSchema: editDepositAccountValidationSchema,
    initialValues: {
      name: depositAccount?.name,
    },
    enableReinitialize: true,
    onSubmit: (values, formActions) => {
      if (values.name.trim() === depositAccount?.name) {
        formActions.setFieldValue('name', values.name.trim());
        setIsEdit(false);

        return;
      }

      mutate(values);
    },
  });

  const closeEdit = () => {
    formik.resetForm();
    setIsEdit(false);
  };

  const openEdit = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditDepositAccountStartRedirect);

    setIsEdit(true);
  };

  return (
    <FormikForm value={formik}>
      <PrimaryDetailsPanel
        variant={COLORS.violet[150]}
        headerSlot={
          <>
            {isEdit ? (
              <FormikInput autoFocus name='name' disabled={!isEdit} variant='small' apiError={data?.error} />
            ) : (
              <PrimaryDetailsPanelHeader>{name || '-'}</PrimaryDetailsPanelHeader>
            )}
            {isActiveDepositAccount && (
              <>
                {isEdit && !isPending && (
                  <Box display='flex' columnGap='12px'>
                    <IconButton type='submit'>
                      <SaveIcon />
                    </IconButton>
                    <IconButton onClick={closeEdit} svgPath='stroke'>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
                {isPending && <Loader width={40} backgroundColor='transparent' />}
                {!isEdit && (
                  <IconButton onClick={openEdit}>
                    <EditIcon />
                  </IconButton>
                )}
              </>
            )}
          </>
        }
      >
        <PrimaryDetailsPanelColumn name='Status'>
          <DepositAccountStatus status={status} />
        </PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Receiving Account ID'>{ref_id || '-'}</PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Created by'>{created_by?.email || '-'}</PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Creation Date'>{formattedDate || '-'}</PrimaryDetailsPanelColumn>
      </PrimaryDetailsPanel>
    </FormikForm>
  );
};
